import {
  Box,
  Center,
  EmptyState as ChakraEmptyState,
  Icon as ChakraIcon,
  VStack,
} from '@chakra-ui/react';
import React from 'react';
import { AiFillFileUnknown, AiOutlineWarning } from 'react-icons/ai';
import type { IconType } from 'react-icons/lib';
import { SkeletonLoading } from './skeleton';
import { useAppSelector } from '@/state';

interface EmptyStateProps {
  isEmpty?: boolean;
  position?: 'absolute' | 'relative';
  title: string;
  description?: string;
  icon?: IconType;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  isEmpty,
  position = 'absolute',
  title,
  description,
  icon,
}) => {
  if (!isEmpty) return null;

  return (
    <Box
      pos={position}
      top={position === 'absolute' ? '0' : 'auto'}
      left={position === 'absolute' ? '0' : 'auto'}
      right={position === 'absolute' ? '0' : 'auto'}
      bottom={position === 'absolute' ? '0' : 'auto'}
      bg="bg/80"
      zIndex={10}
      display={isEmpty ? 'block' : 'none'}
      alignItems="center"
      justifyContent="center"
    >
      <Center
        colorPalette="teal"
        h="full"
        gap={10}
        flexDir="column"
        color="fg.subtle"
      >
        <ChakraEmptyState.Root>
          <ChakraEmptyState.Content>
            <ChakraEmptyState.Indicator>
              <ChakraIcon
                as={icon || AiFillFileUnknown}
                boxSize={20}
                color="fg.subdued"
              />
            </ChakraEmptyState.Indicator>
            <VStack textAlign="center">
              <ChakraEmptyState.Title
                color="fg.subdued"
                fontSize="xl"
                fontWeight={'extrabold'}
              >
                {title || 'No Results'}
              </ChakraEmptyState.Title>
              <ChakraEmptyState.Description
                color="fg.subdued"
                fontSize="md"
                fontWeight={'medium'}
              >
                {description || 'No data is available to display.'}
              </ChakraEmptyState.Description>
            </VStack>
          </ChakraEmptyState.Content>
        </ChakraEmptyState.Root>
      </Center>
    </Box>
  );
};

export const EmptyStateBox: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
}) => {
  return (
    <Center height="100%" width="100%" flex={1}>
      <ChakraEmptyState.Root>
        <ChakraEmptyState.Content>
          <ChakraEmptyState.Indicator>
            <ChakraIcon
              as={icon || AiFillFileUnknown}
              boxSize={20}
              color="fg.subdued"
            />
          </ChakraEmptyState.Indicator>
          <VStack textAlign="center">
            <ChakraEmptyState.Title
              color="fg.subdued"
              fontSize="xl"
              fontWeight={'extrabold'}
            >
              {title || 'No Results'}
            </ChakraEmptyState.Title>
            <ChakraEmptyState.Description
              color="fg.subdued"
              fontSize="md"
              fontWeight={'medium'}
            >
              {description || 'No data is available to display.'}
            </ChakraEmptyState.Description>
          </VStack>
        </ChakraEmptyState.Content>
      </ChakraEmptyState.Root>
    </Center>
  );
};

interface EmptyStateListProps {
  isLoading?: boolean;
  title: string;
  icon?: IconType;
}

export const EmptyStateList: React.FC<EmptyStateListProps> = ({
  isLoading = false,
  title,
  icon,
}) => {
  return (
    <ChakraEmptyState.Root p={0} m={0}>
      <ChakraEmptyState.Content
        flexDirection="row"
        flex={1}
        gap={2}
        alignItems="center"
        justifyContent={isLoading ? 'center' : 'flex-start'}
        width="full"
      >
        {isLoading ? (
          <SkeletonLoading noOfLines={1} />
        ) : (
          <>
            <ChakraEmptyState.Indicator>
              <ChakraIcon
                color="accent1.contrast/50"
                size="sm"
                as={icon || AiOutlineWarning}
              />
            </ChakraEmptyState.Indicator>
            <ChakraEmptyState.Title
              color="accent1.contrast/50"
              textStyle="fieldcontent"
              fontSize="sm"
              fontWeight={'normal'}
            >
              {title || 'No Results'}
            </ChakraEmptyState.Title>
          </>
        )}
      </ChakraEmptyState.Content>
    </ChakraEmptyState.Root>
  );
};

// Used to show an empty state when a required header is not selected
export const EmptyStateForRequiredHeaderSelection = ({
  children,
  requiredHeaderSelection,
}: {
  children: React.ReactNode;
  requiredHeaderSelection: {
    project?: boolean;
    release?: boolean;
    projectSheet?: boolean;
  };
}) => {
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const selectedRelease = useAppSelector(
    (state) => state.release.selectedRelease,
  );
  const selectedProjectSheet = useAppSelector(
    (state) => state.project.selectedProjectSheet,
  );

  if (!selectedProject?.id && requiredHeaderSelection?.project) {
    return (
      <EmptyState
        title="No Project Selected"
        description="Please select a Project to continue"
        isEmpty
        position="relative"
      />
    );
  }

  if (!selectedRelease?.id && requiredHeaderSelection?.release) {
    return (
      <EmptyState
        title="No Release Selected"
        description="Please select a Release to continue"
        isEmpty
        position="relative"
      />
    );
  }

  if (!selectedProjectSheet?.id && requiredHeaderSelection?.projectSheet) {
    return (
      <EmptyState
        title="No Project Sheet Selected"
        description="Please select a Project Sheet to continue"
        isEmpty
        position="relative"
      />
    );
  }

  return <>{children}</>;
};
